import React from 'react';
import { AlertTriangle, DollarSign, FileText, TrendingUp } from 'lucide-react';
import type { CfoSummary } from '../utils/executeChrome';
import {
  formatAssumptionDisplayValue,
  formatCurrencyAmount,
} from '../utils/formatFinancialDisplay';

export interface CfoArtifactAction {
  id: string;
  label: string;
  onClick: () => void;
}

export interface CFOPanelProps {
  cfo: CfoSummary;
  onOpenRoiModel: () => void;
  artifacts?: CfoArtifactAction[];
}

function displayBenefit(value: string): string {
  return value.replace(/\s*annually\s*$/i, '').trim() || value;
}

function formatCellMoney(label: string, raw: string): string {
  const trimmed = raw.trim();
  const usdMatch = trimmed.match(/^([\d,.]+)\s*USD$/i);
  if (usdMatch) {
    return formatCurrencyAmount(Number(usdMatch[1].replace(/,/g, '')), { compact: true });
  }
  return formatAssumptionDisplayValue(label, null, raw);
}

function ConfidenceBadge({ value }: { value: string }) {
  const normalized = value.trim().toLowerCase();
  const tone =
    normalized === 'high'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : normalized === 'low'
        ? 'bg-rose-50 text-rose-700 border-rose-200'
        : 'bg-amber-50 text-amber-700 border-amber-200';

  return (
    <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-md border ${tone}`}>
      {value || '—'} confidence
    </span>
  );
}

export const CFOPanel: React.FC<CFOPanelProps> = ({ cfo, onOpenRoiModel, artifacts = [] }) => {
  const metrics = [
    { label: 'Annual benefit', value: displayBenefit(cfo.benefit), note: cfo.benefitNote },
    { label: 'Investment', value: cfo.cost, note: cfo.costNote },
    { label: 'Payback', value: cfo.payback },
  ];

  return (
    <div className="rounded-xl border border-[#18365E]/15 bg-white p-5 min-w-0 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <DollarSign className="w-4 h-4 text-[#18365E] shrink-0" />
          <span className="text-xs font-semibold text-[#18365E] uppercase tracking-wide truncate">
            CFO Financial Summary
          </span>
        </div>
        <ConfidenceBadge value={cfo.conf} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {metrics.map(({ label, value, note }) => (
          <div key={label} className="rounded-lg bg-[#F8FAFC] border border-[#eef2f7] px-4 py-3 min-w-0">
            <p className="text-[10px] text-[#717182] font-semibold uppercase tracking-wide">{label}</p>
            <p
              className="mt-1.5 text-[15px] font-bold text-[#18365E] leading-snug whitespace-nowrap overflow-hidden text-ellipsis"
              title={value}
            >
              {value}
            </p>
            {note ? <p className="text-[10px] text-[#94a3b8] mt-1">{note}</p> : null}
          </div>
        ))}
      </div>

      {cfo.assumptions.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-2">
            Key assumptions
          </p>
          <ul className="divide-y divide-[#f1f5f9] rounded-lg border border-[#eef2f7] overflow-hidden">
            {cfo.assumptions.map((assumption) => (
              <li
                key={assumption.label}
                className="flex items-start justify-between gap-4 px-3.5 py-2.5 bg-white"
              >
                <div className="min-w-0">
                  <p className="text-xs text-[#4b5563] leading-snug">{assumption.label}</p>
                  <p className="text-[10px] text-[#94a3b8] mt-0.5">{assumption.source}</p>
                </div>
                <p className="text-xs font-bold text-[#18365E] whitespace-nowrap shrink-0">
                  {formatCellMoney(assumption.label, assumption.value)}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {cfo.flags.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-3.5 py-3">
          <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            Needs validation
          </p>
          <ul className="space-y-1">
            {cfo.flags.map((flag) => (
              <li key={flag} className="text-xs text-amber-900/90 leading-relaxed pl-5 relative">
                <span className="absolute left-1.5 top-[0.45em] w-1 h-1 rounded-full bg-amber-600" aria-hidden />
                {flag}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="pt-1 border-t border-[#f1f5f9]">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onOpenRoiModel}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#18365E] hover:bg-[#122a4a] px-3 py-2 rounded-lg transition-colors"
          >
            <TrendingUp className="w-3.5 h-3.5" />
            ROI Modeling
          </button>
          {artifacts.map((artifact) => (
            <button
              key={artifact.id}
              type="button"
              onClick={artifact.onClick}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[#18365E] bg-white border border-[#e5e7eb] hover:border-[#18365E]/40 hover:bg-[#F8FAFC] px-3 py-2 rounded-lg transition-colors"
            >
              <FileText className="w-3.5 h-3.5 shrink-0 opacity-70" />
              {artifact.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CFOPanel;
